<template>
    <div class="teamlead-progress-table">
        <el-table :data="items" :row-key="getRowKey" stripe class="domain-table"
            :show-overflow-tooltip="false" table-layout="fixed" max-height="600">
            <template slot="empty">
                <div v-if="loading" class="loading-body">
                    <i class="el-icon-loading"></i>
                    <span>Đang tải...</span>
                </div>
                <div v-else class="empty-body">
                    <span>Không có dữ liệu</span>
                </div>
            </template>
            <el-table-column label="Thời gian" width="110">
                <template slot-scope="{ row }">
                    <span>Quý {{convertToRoman(row.quarter)}}/{{row.year}}</span>
                </template>
            </el-table-column>
            <el-table-column label="Họ tên" width="200">
                <template slot-scope="{ row }">
                    <span>{{row.user.name}}</span>
                </template>
            </el-table-column>
            <el-table-column label="Mã NS" width="80">
                <template slot-scope="{ row }">
                    <span>{{row.user?.id + 100000 ?? ''}}</span>
                </template>
            </el-table-column>
            <el-table-column label="Team" width="140">
                <template slot-scope="{ row }">
                    <span>{{row.department?.name}}</span>
                </template>
            </el-table-column>
            <el-table-column label="Số EXP" width="100">
                <template slot-scope="{row}">
                    {{ row.exp_points ?? 0 }}
                </template>
            </el-table-column>
            <el-table-column label="Điểm KRA" width="110">
                <template slot-scope="{ row }">
                    <span class="link">{{ parseInt(row.kra_score) ? parseInt(row.kra_score) + '/100' : '0/100'}}</span>
                </template>
            </el-table-column>
            <el-table-column label="Tổng điểm" width="150">
                <template slot-scope="{ row }">
                    <el-tooltip content="Giải thích: Tổng điểm / điểm cần đạt để tăng level" placement="top" effect="light">
                        <span style="color: #4D1EAB; font-weight: 600;">{{ calcTotal(row) }}/ {{ row.requested_level.score }}</span>
                    </el-tooltip>
                </template>
            </el-table-column>

            <el-table-column :min-width="600">
                <template slot="header">
                    <div class="level-scale">
                        <span>Trainee</span>
                        <span>Junior 1</span>
                        <span>Junior 2</span>
                        <span>Member 1</span>
                        <span>Member 2</span>
                        <span>Senior 1</span>
                        <span>Senior 2</span>
                    </div>
                </template>
                <template slot-scope="{row}">
                    <div class="track">
                        <el-tooltip effect="light" placement="right">
                            <div v-if="getPointNeeded(row)" slot="content">
                                Bạn cần đạt <b>{{ getPointNeeded(row) }}</b> điểm để đạt level tiếp theo
                            </div>
                            <div v-else slot="content">
                                Bạn đã đủ điểm để đạt level tiếp theo 😍
                            </div>
                            <div class="bar admin" :style="barWidth(overallWidth(row.current_level.level))"
                            :data-label="currentLabel(row)">
                            </div>
                        </el-tooltip>
                    </div>
                </template>
            </el-table-column>
        </el-table>
    </div>
</template>

<script>
export default {
    name: 'EmployeeProgressTable',
    props: {
        items: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
    },
    data() {
        return {};
    },
    methods: {
        convertToRoman(num) {
            const romanNumerals = {
                1: 'I',
                2: 'II', 
                3: 'III',
                4: 'IV'
            };
            return romanNumerals[num] || num;
        },
        getRowKey(row) {
            return row.id || row.employeeId || `${row.fullName}-${row.periodLabel}`;
        },
        barWidth(percent) {
            const p = Math.max(0, Math.min(100, Number(percent || 0)));
            return { width: p + '%' };
        },
        overallWidth(bands) {
            const order = ['trainee', 'junior1', 'junior2', 'member1', 'member2', 'senior1', 'senior2'];

            if (typeof bands === 'string') {
                const normalized = bands.toLowerCase().trim();
                const level = normalized.match(/(senior|junior|member|trainee)/)?.[1];
                const number = normalized.match(/[12]/)?.[0];

                const normalizedBands = level === 'trainee' ? 'trainee' : `${level}${number || '2'}`;
                const idx = Math.max(0, order.indexOf(normalizedBands));
                // Thay đổi từ (idx + 1) thành (idx + 0.5) để bar kết thúc ở giữa mỗi mốc
                return ((idx + 0.5) / order.length) * 100;
            }

            const totalPercent = order.reduce((sum, key) => {
                const val = Math.max(0, Math.min(100, Number(bands?.[key] ?? 0)));
                return sum + val;
            }, 0);

            return Math.max(0, Math.min(100, (totalPercent / 700) * 100));
        },

        getPointNeeded(item) {
            const kra_score = item.kra_score ?? 0;
            const meeting_points = item.meeting_points ?? 0;
            const exp_points = item.exp_points ?? 0;
            const sum = parseInt(kra_score)+parseInt(meeting_points)+parseInt(exp_points)
            const denominator = parseInt(item.requested_level.score) ?? 0;

            if(denominator - sum > 0) {
                return denominator - sum;
            }
            return 0;
        },
        currentLabel(row) {
            return row.current_level.level;
        },
        calcTotal(row){
            const n = v => (parseInt(v) || 0);
            const kraScore = n(row?.kra_score);
            const meetingPoints = n(row?.meeting_points);
            const expPoints = n(row?.exp_points);
            const totalMinusPoint = n(row?.total_minus_point);
            const sum = kraScore + meetingPoints + expPoints - totalMinusPoint;
            return Math.max(0, sum);
        },
    }
};
</script>

<style lang="scss" scoped>
.track {
    position: relative;
    height: 26px;
}

.track .bar {
    height: 24px;
    border-radius: 20px;
    position: absolute;
    left: 0;
    transition: width .3s ease;
    /* animation only; keep existing visuals */
    transform-origin: left center;
    transform: scaleX(0);
    animation: fill-grow 1.8s ease-in-out forwards;
}


/* labels always inside the bar */
.track .bar::after {
  content: attr(data-label);
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  color: white;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

 .track .bar.admin {
     background: #6D41C6;
 }

 .track .bar.domain {
     background: #6D41C6;
 }

.track .bar.domain {
    top: 0px;
}


::v-deep {
    .domain-table {
        .el-table__header th,
        .el-table__body td,
        .el-table__header th .cell,
        .el-table__body td .cell {
            white-space: normal !important;
            word-break: keep-all !important;
            overflow: visible !important;
            max-width: none !important;
            height: auto !important;
            text-align: center !important;
        }

        .el-table__header th {
            background: #4d1eab !important;
            color: #fff !important;
            border-color: #4d1eab !important;
            line-height: 1.4 !important;
            padding: 16px 8px !important;
            height: 80px !important;
        }

        .el-table__body td {
            line-height: 1.3 !important;
            padding: 8px 4px !important;
            height: auto !important;
        }

        .el-table__body tr {
            height: 60px !important;
        }
    }
}

.level-scale {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    align-items: end;
    gap: 0;
}

.level-scale span {
    text-align: center;
    font-weight: 600;
}

.loading-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #606266;
    
    i {
        font-size: 24px;
        margin-bottom: 10px;
        animation: rotating 2s linear infinite;
    }
    
    span {
        font-size: 14px;
    }
}

.empty-body {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #909399;
    
    span {
        font-size: 14px;
    }
}

@keyframes rotating {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

@keyframes fill-grow {
  0% { transform: scaleX(0); }
  100% { transform: scaleX(1); }
}

.pager {
    display: flex;
    justify-content: center;
    margin-top: 20px;
}
</style>

@keyframes rotating {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

@keyframes fill-grow {
  0% { transform: scaleX(0); }
  100% { transform: scaleX(1); }
}

.pager {
    display: flex;
    justify-content: center;
    margin-top: 20px;
}
</style>
